import { useState, useCallback, useRef, useEffect } from 'react';
import { Combobox, Listbox } from '@headlessui/react';
import { ArrowPathIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { searchSchools } from '../services/SchoolRecordService';
import { getCachedGeo } from '../services/GeoService';
import { debounce } from 'lodash';
import { toTitleCase } from '../utils/string';
import { addNewSchool } from '../services/PricingService';
import LoadingSpinner from './LoadingSpinner';
import { Mixpanel } from '../services/Mixpanel';

const PAGE_SIZE = 20;

// Simple StateDropdown using Headless UI Listbox
export const StateDropdown = ({
  selected,
  setSelected,
  label,
  placeholder = 'Select State',
  labelClassName = 'text-sm font-semibold',
}) => {
  return (
    <Listbox value={selected} onChange={setSelected}>
      {({ open }) => (
        <div className='relative w-full'>
          <Listbox.Label className={labelClassName}>{label}</Listbox.Label>
          <Listbox.Button className='relative w-full cursor-default rounded-md border border-gray-400 bg-white h-[42px] py-2 pl-3 pr-10 text-left focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 sm:text-sm'>
            <span className='block truncate'>{selected?.name || placeholder}</span>
            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              {open ? (
                <ChevronUpIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
              ) : (
                <ChevronDownIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
              )}
            </span>
          </Listbox.Button>

          <Listbox.Options className='absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
            {US_STATES.map((state) => (
              <Listbox.Option
                key={state.value}
                value={state}
                className={({ active, selected }) =>
                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                    selected
                      ? 'bg-emerald-600 text-white font-semibold'
                      : active
                        ? 'bg-emerald-100 text-emerald-900'
                        : 'text-gray-900'
                  }`
                }>
                <span className='block truncate'>{state.name}</span>
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      )}
    </Listbox>
  );
};

export const US_STATES = [
  { name: 'Alabama', value: 'AL' },
  { name: 'Alaska', value: 'AK' },
  { name: 'Arizona', value: 'AZ' },
  { name: 'Arkansas', value: 'AR' },
  { name: 'California', value: 'CA' },
  { name: 'Colorado', value: 'CO' },
  { name: 'Connecticut', value: 'CT' },
  { name: 'Delaware', value: 'DE' },
  { name: 'Florida', value: 'FL' },
  { name: 'Georgia', value: 'GA' },
  { name: 'Hawaii', value: 'HI' },
  { name: 'Idaho', value: 'ID' },
  { name: 'Illinois', value: 'IL' },
  { name: 'Indiana', value: 'IN' },
  { name: 'Iowa', value: 'IA' },
  { name: 'Kansas', value: 'KS' },
  { name: 'Kentucky', value: 'KY' },
  { name: 'Louisiana', value: 'LA' },
  { name: 'Maine', value: 'ME' },
  { name: 'Maryland', value: 'MD' },
  { name: 'Massachusetts', value: 'MA' },
  { name: 'Michigan', value: 'MI' },
  { name: 'Minnesota', value: 'MN' },
  { name: 'Mississippi', value: 'MS' },
  { name: 'Missouri', value: 'MO' },
  { name: 'Montana', value: 'MT' },
  { name: 'Nebraska', value: 'NE' },
  { name: 'Nevada', value: 'NV' },
  { name: 'New Hampshire', value: 'NH' },
  { name: 'New Jersey', value: 'NJ' },
  { name: 'New Mexico', value: 'NM' },
  { name: 'New York', value: 'NY' },
  { name: 'North Carolina', value: 'NC' },
  { name: 'North Dakota', value: 'ND' },
  { name: 'Ohio', value: 'OH' },
  { name: 'Oklahoma', value: 'OK' },
  { name: 'Oregon', value: 'OR' },
  { name: 'Pennsylvania', value: 'PA' },
  { name: 'Rhode Island', value: 'RI' },
  { name: 'South Carolina', value: 'SC' },
  { name: 'South Dakota', value: 'SD' },
  { name: 'Tennessee', value: 'TN' },
  { name: 'Texas', value: 'TX' },
  { name: 'Utah', value: 'UT' },
  { name: 'Vermont', value: 'VT' },
  { name: 'Virginia', value: 'VA' },
  { name: 'Washington', value: 'WA' },
  { name: 'West Virginia', value: 'WV' },
  { name: 'Wisconsin', value: 'WI' },
  { name: 'Wyoming', value: 'WY' },
  { name: 'District of Columbia', value: 'DC' },
];

/**
 * Custom input component that shows a two-line school display with a clear button
 * when a school is selected, otherwise shows a regular search input.
 *
 * @param {Object} props - Component props
 * @param {string} props.inputHeight - Tailwind height class for the input
 * @param {string} props.placeholder - Placeholder text shown when empty
 * @param {Function} props.onInputChange - Called when user types in the input
 * @param {Object|null} props.selectedSchool - The currently selected school object
 * @param {Function} props.onSelectSchool - Called when a school is selected/deselected
 * @param {string} props.searchText - Current search query text
 * @param {Function} props.onChange - Combobox change handler (for internal use)
 * @param {Function} props.onClearSelection - Called when the clear button is clicked
 */
const CustomTwoLineInput = ({
  inputHeight,
  placeholder,
  onInputChange,
  selectedSchool,
  onSelectSchool,
  searchText,
  onClearSelection,
  inputRef,
  ...props
}) => {
  return (
    <div
      className={`w-full ${inputHeight} px-3 text-sm leading-5 flex flex-col justify-center relative`}>
      {selectedSchool ? (
        <div className='flex items-center justify-between'>
          <div className='flex-1 min-w-0 py-px'>
            <span className='block truncate font-medium text-gray-900'>{selectedSchool.name}</span>
            <span className='text-xs text-gray-500'>
              {selectedSchool.city && `${toTitleCase(selectedSchool.city)}`}
              {selectedSchool.state &&
                `, ${selectedSchool.state.length === 2 ? selectedSchool.state.toUpperCase() : toTitleCase(selectedSchool.state)}`}
              {!selectedSchool.state && selectedSchool.country && `, ${selectedSchool.country}`}
            </span>
          </div>
          <button
            type='button'
            className='ml-2 flex-shrink-0 px-1 h-6 flex items-center justify-center hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors rounded'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Clear the selection using the provided handler
              if (onClearSelection) {
                onClearSelection();
              } else {
                onSelectSchool(null);
              }
              setTimeout(() => {
                inputRef?.current?.focus();
              }, 0);
            }}
            aria-label='Clear selection'
            title='Clear selection'>
            clear
          </button>
        </div>
      ) : (
        <input
          {...props}
          className='w-full border-none focus:outline-none'
          placeholder={placeholder}
          value={searchText || ''}
          onChange={(e) => onInputChange(e.target.value)}
        />
      )}
    </div>
  );
};

export const SchoolSearch = ({
  selectedSchool,
  onSelectSchool,
  disabled,
  onAddSchoolFormShown,
  onAddSchoolFormHidden,
  askForEnrollment,
  addSchoolToSalesforce = false,
  mixpanelLocation = '',
  labelClassName = 'sr-only',
  labelText = 'Organization Name',
  placeholder = 'School or District Name',
  optionsMaxHeight = 'max-h-52',
  inputHeight = 'h-[42px]',
  showCancelButton = false,
  searchDistricts = false,
  filterState = undefined,
  allowAddOrganization = true,
}) => {
  const [filters, setFilters] = useState({
    schoolNameSearchText: selectedSchool
      ? `${selectedSchool?.name}${selectedSchool?.city ? ` (${selectedSchool.city})` : ''}`
      : '',
    locationNameSearchText: '',
    page: 1,
    googleLocation: undefined,
  });

  useEffect(() => {
    if (disabled) {
      setFilters({
        ...filters,
        schoolNameSearchText: '',
      });
    }
  }, [disabled]);

  const [schools, setSchools] = useState(undefined); // undefined means we're waiting for a search query. an empty list means we've searched and found no results
  const [isLoadingSchools, setIsLoadingSchools] = useState(false);
  const [isSearchDebouncePending, setIsSearchDebouncePending] = useState(false);
  const [addSchoolInitialState, setAddSchoolInitialState] = useState(null); // { name: string, location: { city: string, state: string, country: string } }

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreSchools, setHasMoreSchools] = useState(true);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);

  const handleSearch = useCallback(
    debounce(
      async (searchQuery, googleLocation, newPage) => {
        if (!searchQuery.trim()) {
          setSchools(undefined);
          setCurrentPage(1);
          setHasMoreSchools(true);
          setIsSearchDebouncePending(false);
          return;
        }

        setIsLoadingSchools(true);

        try {
          const res = await searchSchools({
            name: searchQuery,
            location: googleLocation,
            page: newPage,
            pageSize: PAGE_SIZE,
            districtsOnly: searchDistricts,
            filterState,
            // Read per search rather than once at mount: geo lands asynchronously at
            // boot, so a search typed early would otherwise send nothing.
            ...getCachedGeo(),
          });

          // If it's page 1, replace the schools list, otherwise append
          setSchools((prevSchools) =>
            newPage === 1 ? res.data.records : [...(prevSchools || []), ...res.data.records]
          );

          setHasMoreSchools(res.data.pages > newPage);
          setCurrentPage(newPage);
        } catch (error) {
          console.error('Error searching schools:', error);
        } finally {
          setIsLoadingSchools(false);
          setIsSearchDebouncePending(false);
        }
      },
      500,
      { leading: false, trailing: true }
    ),
    []
  );

  const handleInputChange = async (value) => {
    setFilters({
      ...filters,
      schoolNameSearchText: value,
    });
    if (selectedSchool) {
      onSelectSchool(null);
    }
    setIsSearchDebouncePending(true);
    await handleSearch(value, filters.googleLocation, 1);
  };

  // Main Combobox onChange handler
  const handleComboboxChange = useCallback(
    (school) => {
      if (school) {
        Mixpanel.track('School selected from search', {
          school: school.name,
          location: mixpanelLocation,
        });
        onSelectSchool(school);
      } else {
        onSelectSchool(null);
      }
    },
    [onSelectSchool, mixpanelLocation]
  );

  if (addSchoolInitialState) {
    return (
      <div>
        <AddSchoolForm
          onSchoolAdded={(school) => {
            onSelectSchool(school);
            setAddSchoolInitialState(null);
            onAddSchoolFormHidden?.();
          }}
          initialState={addSchoolInitialState}
          onDone={() => {
            setAddSchoolInitialState(null);
            onAddSchoolFormHidden?.();
          }}
          askForEnrollment={askForEnrollment}
          mixpanelLocation={mixpanelLocation}
          labelClassName={labelClassName}
          addSchoolToSalesforce={addSchoolToSalesforce}
        />
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <Combobox
        value={selectedSchool}
        onChange={handleComboboxChange}
        key={selectedSchool ? 'with-school' : 'no-school'}>
        {() => (
          <div className='relative'>
            <div className='flex flex-col gap-1'>
              <Combobox.Label id='organization-name-label' className={labelClassName}>
                {labelText}
              </Combobox.Label>
              <div className='relative w-full cursor-default overflow-hidden rounded-md border border-gray-500 text-left focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500'>
                {selectedSchool ? (
                  // When school is selected, show custom two-line display
                  <Combobox.Input
                    as={CustomTwoLineInput}
                    aria-label='Search for a school or district'
                    inputHeight={inputHeight}
                    placeholder={placeholder}
                    onInputChange={handleInputChange}
                    selectedSchool={selectedSchool}
                    onSelectSchool={onSelectSchool}
                    searchText={filters.schoolNameSearchText}
                    onClearSelection={() => handleComboboxChange(null)}
                    onChange={handleComboboxChange}
                    inputRef={inputRef}
                  />
                ) : (
                  // When no school is selected, show standard input
                  <Combobox.Input
                    ref={inputRef}
                    aria-label='Search for a school or district'
                    className={`w-full border-none ${inputHeight} placeholder:text-gray-400 placeholder:text-light placeholder:text-sm px-3 text-sm leading-5 focus:ring-0`}
                    placeholder={placeholder}
                    displayValue={(school) => {
                      if (school) {
                        return `${school.name} (${school.city})`;
                      }
                      return '';
                    }}
                    onChange={async (event) => {
                      await handleInputChange(event.target.value);
                    }}
                  />
                )}
              </div>
            </div>

            <div
              className={`${!filters.schoolNameSearchText ? 'opacity-0 pointer-events-none' : ''}`}>
              <Combobox.Options
                className={`absolute z-10 mt-1 w-full bg-white rounded-md ${schools && schools.length > 0 ? 'shadow-lg border' : ''} overflow-auto focus:outline-none sm:text-sm`}>
                <div
                  ref={scrollContainerRef}
                  onScroll={() => {
                    // if we've hit near the bottom of the list, load more if possible
                    if (
                      hasMoreSchools &&
                      scrollContainerRef.current.scrollTop +
                        scrollContainerRef.current.clientHeight >=
                        scrollContainerRef.current.scrollHeight - 20
                    ) {
                      Mixpanel.track('School search - More schools loaded', {
                        query: filters.schoolNameSearchText,
                        location: mixpanelLocation,
                        page: currentPage + 1,
                        is_action: false,
                      });
                      handleSearch(
                        filters.schoolNameSearchText,
                        filters.googleLocation,
                        currentPage + 1
                      );
                    }
                  }}
                  className={`${optionsMaxHeight} overflow-y-auto`}>
                  {schools?.map((school, index) => (
                    <Combobox.Option
                      key={`${school.salesforce_id}-${index}`}
                      className={({ active }) =>
                        `relative cursor-pointer select-none p-2 pr-4 text-gray-900 ${active ? 'bg-gray-100' : 'hover:bg-gray-100'}`
                      }
                      value={school}>
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {school.name}
                          </span>
                          <span className='text-xs text-gray-500'>
                            {school.city && `${toTitleCase(school.city)}`}
                            {school.state &&
                              `, ${school.state.length === 2 ? school.state.toUpperCase() : toTitleCase(school.state)}`}
                            {!school.state && school.country && `, ${school.country}`}
                          </span>
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                  <div>
                    {isLoadingSchools && (
                      <div className='flex items-center gap-2 w-full text-left py-2 ps-2 pr-4 text-xs bg-gray-100 text-gray-500 hover:bg-gray-200'>
                        <ArrowPathIcon className='w-4 h-4 animate-spin' />
                        Loading more schools...
                      </div>
                    )}
                  </div>
                </div>
                {allowAddOrganization &&
                  schools &&
                  filters.schoolNameSearchText &&
                  !isLoadingSchools &&
                  !isSearchDebouncePending && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setAddSchoolInitialState({
                          schoolName: filters.schoolNameSearchText,
                          location: filters.googleLocation,
                        });
                        Mixpanel.track('Add school or district selected from search', {
                          query: filters.schoolNameSearchText,
                          location: mixpanelLocation,
                        });
                        onAddSchoolFormShown?.();
                      }}
                      className='py-2 flex items-center rounded-b-md  w-full text-left ps-2 pr-4 text-xs bg-gray-100 text-gray-500 hover:bg-gray-200 border-t border-gray-200'>
                      Can't find your organization? Add it{' '}
                      <span className='inline-block'>
                        <ChevronRightIcon className='w-4 h-4 self-center' />
                      </span>
                    </button>
                  )}
              </Combobox.Options>
            </div>
          </div>
        )}
      </Combobox>
      {showCancelButton && (
        <div className='flex justify-center'>
          <button
            className='text-red-700 hover:text-red-500 rounded-md text-sm px-3 py-2'
            onClick={() => {
              Mixpanel.track('School search - Cancel clicked', {
                location: mixpanelLocation,
              });
              onAddSchoolFormHidden?.();
            }}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

const LabelledTextInput = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  forwardRef,
  placeholder,
  subText = '',
  labelClassName = 'text-sm font-semibold',
  maxLength,
}) => {
  return (
    <div>
      <label className={`${labelClassName}`}>
        {label} {subText ? <span className='text-sm font-normal italic'>{subText}</span> : ''}
      </label>
      <input
        id={id}
        className='w-full border rounded-md px-3 py-2 border-gray-400 focus:border-emerald-500 focus:ring-emerald-500 placeholder:text-gray-400 placeholder:text-sm'
        label={label}
        placeholder={placeholder}
        value={value}
        type={type}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onBlur={onBlur}
        ref={forwardRef}
        maxLength={maxLength}
      />
    </div>
  );
};

const AddSchoolForm = ({
  onSchoolAdded,
  initialState,
  onDone,
  askForEnrollment,
  mixpanelLocation,
  labelClassName = 'text-sm font-semibold',
  addSchoolToSalesforce = false,
}) => {
  const [formFields, setFormFields] = useState({
    schoolName: initialState?.schoolName || '',
    city: initialState?.location?.city || '',
    state: initialState?.location?.state || '',
    country: initialState?.location?.country || 'USA', // Default to USA to normaliza the data
    enrollment: '',
  });

  const [isAddingSchool, setIsAddingSchool] = useState(false);

  const isFormDisabled =
    isAddingSchool ||
    !formFields.schoolName ||
    !formFields.city ||
    (askForEnrollment && !(Number(formFields.enrollment) > 0)) ||
    (!formFields.country && !formFields.state); // Country OR state is required

  return (
    <div className='gap-y-2 px-3 py-2 -my-3'>
      {/* TOP-LEVEL SCHOOL SEARCH CONTAINER - Wraps the entire school form */}
      <div className='flex flex-col gap-y-1 px-1'>
        <LabelledTextInput
          id='school-name-input'
          labelClassName={labelClassName}
          label='School Name'
          value={formFields.schoolName}
          onChange={(value) => setFormFields({ ...formFields, schoolName: value })}
          maxLength={120}
        />
        <LabelledTextInput
          id='city-input'
          labelClassName={labelClassName}
          label='City'
          value={formFields.city}
          onChange={(value) => setFormFields({ ...formFields, city: value })}
          maxLength={40}
        />
        <StateDropdown
          selected={US_STATES.find((state) => state.value === formFields.state) || null}
          setSelected={(selectedState) => {
            setFormFields({ ...formFields, state: selectedState?.value || '' });
          }}
          label='State (if in USA)'
          placeholder='Select State'
          labelClassName={labelClassName}
        />
        <LabelledTextInput
          id='country-input'
          labelClassName={labelClassName}
          label='Country'
          value={formFields.country}
          onChange={(value) => setFormFields({ ...formFields, country: value })}
          maxLength={80}
        />
        {askForEnrollment && (
          <div>
            <LabelledTextInput
              labelClassName={labelClassName}
              id='enrollment-input'
              label='Enrollment'
              type='number'
              value={formFields.enrollment}
              onChange={(value) => setFormFields({ ...formFields, enrollment: value })}
            />
            {formFields.enrollment !== '' && !(Number(formFields.enrollment) > 0) && (
              <p className='text-red-500 text-xs mt-1'>Enrollment must be greater than zero</p>
            )}
          </div>
        )}
      </div>
      <div className='flex gap-x-4 justify-between mt-4 mb-2'>
        <button
          className={`${isAddingSchool ? 'opacity-0' : 'text-red-700 hover:text-red-500'}  rounded-md text-sm mx-3`}
          onClick={() => {
            Mixpanel.track('Add school or district - Cancel', {
              query: formFields.schoolName,
              location: mixpanelLocation,
            });
            onDone();
          }}>
          Cancel
        </button>
        <button
          disabled={isFormDisabled}
          className={`w-full px-3 py-2 font-semibold  text-white rounded-md ${
            isFormDisabled ? 'bg-gray-300' : 'hover:bg-emerald-500 bg-emerald-600'
          }`}
          onClick={async () => {
            setIsAddingSchool(true);
            try {
              const response = await addNewSchool({
                ...formFields,
                addSchoolToSalesforce,
              });
              if (response.data.success) {
                const newSchoolRecord = {
                  id: response.data.school_record_id,
                  salesforce_id: response.data.salesforce_id,
                  name: formFields.schoolName,
                  city: formFields.city,
                  state: formFields.state,
                  country: formFields.country,
                  enrollment: formFields.enrollment,
                };
                Mixpanel.track('School info form submitted ', {
                  school: formFields.schoolName,
                  location: mixpanelLocation,
                });
                onSchoolAdded(newSchoolRecord);
                onDone();
              }
            } catch (e) {
              console.error('Error adding school:', e);
            }
            setIsAddingSchool(false);
          }}>
          <p className=''>
            {isAddingSchool && (
              <span className='inline-block me-2'>
                <LoadingSpinner />
              </span>
            )}
            {isAddingSchool ? 'Adding School...' : 'Add School'}
          </p>
        </button>
      </div>
    </div>
  );
};
